import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

function App() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(600);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [manualCooldown, setManualCooldown] = useState(0); // 手动刷新冷却时间（秒）

  const fetchMetrics = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = forceRefresh ? '/api/metrics?refresh=true' : '/api/metrics';
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      setMetrics(data);
      setLastRefresh(new Date());
      setCountdown(600);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchMetrics();
          return 600;
        }
        return prev - 1;
      });
      // 同时更新手动刷新冷却时间
      setManualCooldown(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const formatNumber = (num, decimals = 2) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  const formatCurrency = (num, decimals = 2) => {
    return '$' + formatNumber(num, decimals);
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDiscountColor = (rate) => {
    if (rate > 20) return '#00ff88';
    if (rate > 10) return '#88ff00';
    if (rate > 0) return '#ffff00';
    if (rate > -10) return '#ffaa00';
    return '#ff4444';
  };

  const getDiscountLabel = (rate) => {
    if (rate > 0) return '折价';
    if (rate < 0) return '溢价';
    return '平价';
  };

  if (error && !metrics) {
    return (
      <div className="app">
        <div className="error-container">
          <h1>AURE 折价率分析器</h1>
          <div className="error-message">
            <p>数据加载失败: {error}</p>
            <button onClick={() => fetchMetrics(true)}>重试</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1>AURE 折价率分析器</h1>
        <p className="subtitle">基于黄金持仓的公允价值计算</p>
      </header>

      <main className="main">
        {/* Main Discount Rate Card */}
        <div className="discount-card">
          <div className="discount-header">
            <span className="label">当前折价率</span>
            <span
              className="discount-badge"
              style={{ backgroundColor: metrics ? getDiscountColor(metrics.discountRate) : '#666' }}
            >
              {metrics ? getDiscountLabel(metrics.discountRate) : '...'}
            </span>
          </div>
          <div
            className="discount-value mono"
            style={{ color: metrics ? getDiscountColor(metrics.discountRate) : '#666' }}
          >
            {loading && !metrics ? '...' : metrics ? `${metrics.discountRate > 0 ? '+' : ''}${formatNumber(metrics.discountRate)}%` : 'N/A'}
          </div>
          <div className="discount-meaning">
            {metrics && metrics.discountRate > 0 && (
              <span>当前股价低于公允价值 {formatNumber(metrics.discountRate)}%，可能被低估</span>
            )}
            {metrics && metrics.discountRate < 0 && (
              <span>当前股价高于公允价值 {formatNumber(Math.abs(metrics.discountRate))}%，可能被高估</span>
            )}
            {metrics && metrics.discountRate === 0 && (
              <span>当前股价等于公允价值</span>
            )}
          </div>
        </div>

        {/* Price Comparison */}
        <div className="price-grid">
          <div className="price-card current">
            <div className="price-label">当前股价</div>
            <div className="price-value mono">
              {loading && !metrics ? '...' : metrics ? formatCurrency(metrics.currentPrice, 4) : 'N/A'}
            </div>
            <div className="price-source">NYSE American: AURE</div>
          </div>

          <div className="price-card fair">
            <div className="price-label">公允价值</div>
            <div className="price-value mono">
              {loading && !metrics ? '...' : metrics ? formatCurrency(metrics.fairValue, 4) : 'N/A'}
            </div>
            <div className="price-source">基于黄金净资产</div>
          </div>
        </div>

        {/* Details Panel */}
        <div className="details-panel">
          <h2>计算详情</h2>

          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">黄金持有量</span>
              <span className="detail-value mono">
                {metrics ? formatNumber(metrics.goldHoldings, 1) : '...'} 盎司
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">黄金现价</span>
              <span className="detail-value mono">
                {metrics ? formatCurrency(metrics.goldPrice) : '...'} /盎司
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">黄金总价值</span>
              <span className="detail-value mono highlight-gold">
                {metrics ? formatCurrency(metrics.goldValue, 0) : '...'}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">总负债</span>
              <span className="detail-value mono highlight-red">
                {metrics ? formatCurrency(metrics.totalLiabilities, 0) : '...'}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">净资产价值</span>
              <span className="detail-value mono highlight-green">
                {metrics ? formatCurrency(metrics.netAssetValue, 0) : '...'}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">总股数</span>
              <span className="detail-value mono">
                {metrics ? formatNumber(metrics.totalShares, 0) : '...'} 股
              </span>
            </div>
          </div>

          <div className="formula">
            <h3>计算公式</h3>
            <code className="mono">
              公允价值 = (黄金持有量 × 黄金价格 - 总负债) ÷ 总股数
            </code>
            <code className="mono calculation">
              {metrics ?
                `= (${formatNumber(metrics.goldHoldings, 1)} × ${formatCurrency(metrics.goldPrice)} - ${formatCurrency(metrics.totalLiabilities, 0)}) ÷ ${formatNumber(metrics.totalShares, 0)}`
                : '= ...'}
            </code>
            <code className="mono result">
              {metrics ? `= ${formatCurrency(metrics.fairValue, 4)}` : '= ...'}
            </code>
          </div>
        </div>

        {/* Refresh Info */}
        <div className="refresh-panel">
          <div className="refresh-info">
            <span>下次刷新: </span>
            <span className="countdown mono">{formatCountdown(countdown)}</span>
          </div>
          <button
            className="refresh-button"
            onClick={() => {
              fetchMetrics(true);
              setManualCooldown(180); // 3分钟冷却时间
            }}
            disabled={loading || manualCooldown > 0}
          >
            {loading ? '刷新中...' : manualCooldown > 0 ? `请等待 ${formatCountdown(manualCooldown)}` : '立即刷新'}
          </button>
          {lastRefresh && (
            <div className="last-update">
              上次更新: {lastRefresh.toLocaleTimeString('zh-CN')}
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <p>数据来源: Yahoo Finance (股价), 公开黄金价格API</p>
        <p>免责声明: 本工具仅供参考，不构成投资建议</p>
      </footer>
    </div>
  );
}

export default App;
